// The Eixample blocks with their façades, and the park.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { PI } from '../lib/math.js';
import { pick, rnd, rr } from '../lib/random.js';
import { add, smooth } from '../lib/factory.js';
import { boxW, octPts, octShape, slabGeo } from '../lib/shapes.js';
import { wallMats } from '../materials/walls.js';
import {
  corniceMat,
  curbMat,
  grassMat,
  gravelMat,
  lawnMat,
  lawnSide,
  pondMat,
  roofMat,
  shopMat,
  stoneSmooth,
  terraMat,
  walkMat,
  waterMat,
} from '../materials/surfaces.js';
import { casaBatllo } from './landmarks.js';

export let trees,
  colliders,
  BLOCK,
  CH,
  HOLE,
  HCH,
  PLATE,
  PCH,
  wins,
  shops,
  containers,
  blocks,
  benches,
  plaques,
  parked,
  roof,
  SHOPS,
  eixampleBlock,
  tufts,
  flowers,
  hedges,
  pads,
  palmSpots,
  ducks,
  jets,
  park;

export function buildEixample() {
  eixampleBlock = function eixampleBlock(cx, cz, batllo) {
    add(slabGeo(octShape(PLATE, PCH), 0.3, 0.06), walkMat, cx, 0, cz);
    const h = rr(5.2, 7),
      wall = pick(wallMats),
      top = 0.3 + h - 0.3;
    add(slabGeo(octShape(BLOCK, CH, HOLE, HCH), h, 0.12), [roofMat, wall], cx, 0.3, cz);
    add(
      slabGeo(octShape(BLOCK + 0.5, CH + 0.2, BLOCK - 0.7, CH - 0.3), 0.22, 0.05),
      corniceMat,
      cx,
      0.3 + h - 0.1,
      cz,
    );
    add(
      slabGeo(octShape(HOLE + 0.7, HCH + 0.3, HOLE - 0.5, HCH - 0.2), 0.22, 0.05),
      corniceMat,
      cx,
      0.3 + h - 0.1,
      cz,
    );
    add(
      slabGeo(octShape(BLOCK + 0.3, CH + 0.12, BLOCK - 0.2, CH - 0.05), 0.32, 0.02),
      corniceMat,
      cx,
      0.3 + h + 0.1,
      cz,
    );
    // moulded string course at every floor line
    const rings = [];
    for (let y = 2.2; y < top - 0.2; y += 0.9)
      rings.push(
        slabGeo(octShape(BLOCK + 0.28, CH + 0.12, BLOCK - 0.5, CH - 0.2), 0.06).translate(0, y, 0),
      );
    if (rings.length) add(mergeGeometries(rings), corniceMat, cx, 0, cz);
    add(
      slabGeo(octShape(BLOCK + 0.3, CH + 0.15, HOLE - 0.3, HCH - 0.1), 0.95),
      shopMat,
      cx,
      0.3,
      cz,
    );
    add(
      slabGeo(octShape(BLOCK + 0.4, CH + 0.18, HOLE - 0.4, HCH - 0.15), 0.1),
      corniceMat,
      cx,
      1.25,
      cz,
    );
    colliders.push({ type: 'oct', cx, cz, h: BLOCK / 2 + 0.2, ch: CH, top: 0.3 + h + 2.4 });
    blocks.push({ cx, cz });
    add(slabGeo(octShape(HOLE - 0.4, HCH - 0.2), 0.35), grassMat, cx, 0.3, cz);
    trees.push({ x: cx + rr(-3, 3), z: cz + rr(-3, 3), s: rr(0.8, 1.1) });
    const pts = octPts(BLOCK, CH).map(([px, py]) => [cx + px, cz - py]);
    for (let e = 0; e < 8; e++) {
      const [ax, az] = pts[e],
        [bx, bz] = pts[(e + 1) % 8];
      let nx = (ax + bx) / 2 - cx,
        nz = (az + bz) / 2 - cz;
      const nl = Math.hypot(nx, nz);
      nx /= nl;
      nz /= nl;
      facade(ax, az, bx, bz, nx, nz, 1.75, top, true);
      // (the wall's bevel puts its face .12 out, so skins sit at .125 and pilasters at .14)
      // a block is a row of separate buildings: split the façade into plots of 3-6 bays, each with its own stucco
      // colour (a skin just proud of the wall) and a stone pilaster at every party wall. Bays stay on the shared grid.
      {
        const len = Math.hypot(bx - ax, bz - az),
          bays = Math.max(1, Math.floor(len / 1.3)),
          ry = Math.atan2(nx, nz),
          hgt = top - 1.3;
        const at = (t, off) => [ax + (bx - ax) * t + nx * off, az + (bz - az) * t + nz * off];
        let b0 = 0;
        while (b0 < bays) {
          const b1 = Math.min(bays, b0 + 3 + Math.floor(rnd() * 4)),
            [x, z] = at((b0 + b1) / 2 / bays, 0.125);
          if (rnd() < 0.75) {
            const sk = add(
              boxW(((b1 - b0) / bays) * len, hgt, 0.01),
              pick(wallMats),
              x,
              1.3 + hgt / 2,
              z,
            );
            sk.rotation.y = ry;
            sk.castShadow = false;
          }
          if (b1 < bays) {
            const [px, pz] = at(b1 / bays, 0.14);
            const pl = add(
              new THREE.BoxGeometry(0.14, hgt, 0.07),
              corniceMat,
              px,
              1.3 + hgt / 2,
              pz,
            );
            pl.rotation.y = ry;
            pl.castShadow = false;
          }
          b0 = b1;
        }
      }
      if (e % 2) {
        const sx = Math.sign(nx),
          sz = Math.sign(nz),
          alongX = rnd() < 0.5;
        const name = alongX ? 'de ' + XST[String(cx + sx * 15)] : 'de ' + ZST[String(cz + sz * 15)];
        if (!name.includes('undefined'))
          plaques.push({
            x: (ax + bx) / 2 + nx * 0.17,
            z: (az + bz) / 2 + nz * 0.17,
            ry: Math.atan2(nx, nz),
            name:
              'Carrer ' +
              name
                .replace('de la', 'de la')
                .replace('de ', /^[AEIOU]/i.test(name.slice(3)) ? "d'" : 'de '),
          });
        // parked motorbikes on the chamfer, Barcelona style
        if (rnd() < 0.55)
          for (let u = -2.4; u <= 2.4; u += 0.95)
            parked.push({
              x: cx + sx * 10.9 + (sx * u) / Math.SQRT2,
              z: cz + sz * 10.9 - (sz * u) / Math.SQRT2,
              ry: Math.atan2(sx, sz) + PI + rr(-0.08, 0.08),
            });
      }
      // some long façades get a taller building aligned to the same bay grid
      if (e % 2 === 0 && rnd() < 0.55) {
        const bays = Math.floor(Math.hypot(bx - ax, bz - az) / 1.3),
          s0 = Math.floor(rnd() * (bays - 4)),
          t0 = s0 / bays,
          t1 = (s0 + 4) / bays;
        const th = h + rr(0.9, 2.3),
          d = 4.9,
          m = pick(wallMats),
          along = (t1 - t0) * Math.hypot(bx - ax, bz - az);
        const mx = ax + ((bx - ax) * (t0 + t1)) / 2,
          mz = az + ((bz - az) * (t0 + t1)) / 2;
        const box = add(
          boxW(along, th, d),
          [m, m, terraMat, terraMat, m, m],
          mx + nx * (0.03 - d / 2),
          0.3 + th / 2,
          mz + nz * (0.03 - d / 2),
        );
        box.rotation.y = Math.atan2(nx, nz);
        const cor = add(
          new THREE.BoxGeometry(along + 0.3, 0.2, d + 0.3),
          corniceMat,
          mx + nx * (0.03 - d / 2),
          0.3 + th,
          mz + nz * (0.03 - d / 2),
        );
        cor.rotation.y = box.rotation.y;
        let yStart = 1.75;
        while (yStart + 0.4 < top) yStart += 0.9;
        facade(
          ax + (bx - ax) * t0,
          az + (bz - az) * t0,
          ax + (bx - ax) * t1,
          az + (bz - az) * t1,
          nx,
          nz,
          yStart,
          0.3 + th - 0.3,
          false,
        );
      }
    }
    const inOct = (x, z, H, C) =>
      Math.abs(x) < H && Math.abs(z) < H && Math.abs(x) + Math.abs(z) < 2 * H - C;
    const spot = () => {
      let x, z;
      do {
        x = rr(-10.3, 10.3);
        z = rr(-10.3, 10.3);
      } while (!inOct(x, z, 10.3, 4) || inOct(x, z, 6.8, 2.2));
      return [cx + x, cz + z];
    };
    const ry = 0.3 + h + 0.12;
    for (let k = 0; k < 2; k++) {
      const [x, z] = spot();
      roof.huts.push({ x, z, y: ry, r: rnd() < 0.5 ? 0 : PI / 2 });
    }
    for (let k = 0; k < 2; k++) {
      const [x, z] = spot();
      roof.tanks.push({ x, z, y: ry });
    }
    for (let k = 0; k < 4; k++) {
      const [x, z] = spot();
      roof.antennas.push({ x, z, y: ry, r: rr(0, PI) });
    }
    for (let k = 0; k < 2; k++) {
      const [x, z] = spot();
      roof.dishes.push({ x, z, y: ry, r: rr(0, PI * 2) });
    }
    if (rnd() < 0.4) {
      const [x, z] = spot();
      for (let k = 0; k < 3; k++) roof.panels.push({ x: x + k * 0.75, z, y: ry });
    }
    for (let k = 0; k < 2; k++) {
      const [x, z] = spot();
      roof.laundry.push({ x, z, y: ry, r: rr(0, PI) });
    }
    for (let k = 0; k < 3; k++) {
      const [x, z] = spot();
      roof.chimneys.push({ x, z, y: ry, r: rr(0, PI) });
    }
    for (let k = 0; k < 4; k++) {
      const [x, z] = spot();
      roof.vents.push({ x, z, y: ry });
    }
    for (let k = 0; k < 3; k++) {
      const [x, z] = spot();
      roof.wells.push({ x, z, y: ry, r: rnd() < 0.5 ? 0 : PI / 2 });
    }
    // courtyard façades: windows facing into the block interior
    const ipts = octPts(HOLE, HCH).map(([px, py]) => [cx + px, cz - py]);
    for (let e = 0; e < 8; e++) {
      const [ax, az] = ipts[e],
        [bx, bz] = ipts[(e + 1) % 8];
      let nx = cx - (ax + bx) / 2,
        nz = cz - (az + bz) / 2;
      const nl = Math.hypot(nx, nz);
      nx /= nl;
      nz /= nl;
      facade(ax, az, bx, bz, nx, nz, 1.75, top, false);
    }
    if (batllo) casaBatllo(cx, cz, h);
    // recycling containers on two of the chamfered corners
    for (const [sx, sz] of [
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1],
    ])
      if (rnd() < 0.45)
        for (const u of [-2.9, -1.95, 1.95, 2.9])
          containers.push({
            x: cx + sx * 9.95 + (sx * u) / Math.SQRT2,
            z: cz + sz * 9.95 - (sz * u) / Math.SQRT2,
            ry: Math.atan2(sx, sz),
          });
  };
  park = function park(cx, cz, pond) {
    add(slabGeo(octShape(PLATE, PCH), 0.35, 0.06), [gravelMat, curbMat], cx, 0, cz);
    const W = (th, r) => [cx + Math.cos(th) * r, cz - Math.sin(th) * r];
    const inner = (th) => (pond ? 1 / Math.hypot(Math.cos(th) / 10.4, Math.sin(th) / 7.4) : 4.4),
      outer = (th) => octR(th, 11.3, 4.4);
    // four lawns on the diagonals, leaving gravel paths along the axes and around the centre
    for (let q = 0; q < 4; q++) {
      const a0 = (q * PI) / 2 + 0.13,
        a1 = ((q + 1) * PI) / 2 - 0.13,
        pts = [];
      for (let i = 0; i <= 28; i++) {
        const th = a0 + ((a1 - a0) * i) / 28;
        pts.push([Math.cos(th) * outer(th), Math.sin(th) * outer(th)]);
      }
      for (let i = 28; i >= 0; i--) {
        const th = a0 + ((a1 - a0) * i) / 28;
        pts.push([Math.cos(th) * inner(th), Math.sin(th) * inner(th)]);
      }
      const sh = new THREE.Shape();
      sh.moveTo(...pts[0]);
      pts.slice(1).forEach((p) => sh.lineTo(...p));
      sh.closePath();
      add(slabGeo(sh, 0.06, 0.04), [lawnMat, lawnSide], cx, 0.37, cz);
      for (let th = a0 + 0.04; th < a1 - 0.04; th += 0.85 / outer(th)) {
        const [x, z] = W(th, outer(th) - 0.45);
        hedges.push({ x, z, ry: th + PI / 2 });
      }
      if (q % 2 === 0)
        for (let k = 0; k < 160; k++) {
          const th = rr(a0 + 0.05, a1 - 0.05),
            [x, z] = W(th, inner(th) + rr(0.35, 1));
          flowers.push({ x, z, s: rr(0.7, 1.3) });
        }
      for (let k = 0; k < 260; k++) {
        const th = rr(a0 + 0.03, a1 - 0.03),
          [x, z] = W(th, rr(inner(th) + 0.3, outer(th) - 0.9));
        tufts.push({ x, z, s: rr(0.6, 1.4), r: rr(0, PI * 2) });
      }
      for (let k = 0; k < 3; k++) {
        const th = rr(a0 + 0.15, a1 - 0.15),
          [x, z] = W(th, rr(inner(th) + 1.8, outer(th) - 1.6));
        trees.push({ x, z, s: rr(0.9, 1.25) });
      }
      if (q % 2) {
        const th = (a0 + a1) / 2,
          [x, z] = W(th, (inner(th) + outer(th)) / 2);
        palmSpots.push({ x, z });
      }
    }
    if (pond) {
      const ring = new THREE.Shape();
      ring.absellipse(0, 0, 9.3, 6.3, 0, PI * 2, false);
      const hole = new THREE.Path();
      hole.absellipse(0, 0, 8.6, 5.6, 0, PI * 2, true);
      ring.holes.push(hole);
      add(smooth(slabGeo(ring, 0.16, 0.06)), stoneSmooth, cx, 0.37, cz);
      const wat = new THREE.Shape();
      wat.absellipse(0, 0, 8.7, 5.7, 0, PI * 2);
      add(new THREE.ShapeGeometry(wat, 48).rotateX(-PI / 2), pondMat, cx, 0.47, cz).castShadow =
        false;
      for (let k = 0; k < 16; k++) {
        const th = rr(0, PI * 2),
          r = rr(0.45, 0.9);
        pads.push({
          x: cx + Math.cos(th) * 7.6 * r,
          z: cz + Math.sin(th) * 4.8 * r,
          s: rr(0.7, 1.2),
          r: rr(0, PI * 2),
        });
      }
      for (let k = 0; k < 3; k++)
        ducks.push({ cx, cz, a: rr(0, PI * 2), r: rr(2.5, 4.5), v: rr(0.08, 0.15) });
      colliders.push({ type: 'ell', cx, cz, a: 9.3, b: 6.3 });
    } else {
      // tiered fountain with arcing water jets
      const lathe = (pts, m, y) =>
        add(
          new THREE.LatheGeometry(
            pts.map(([x, yy]) => new THREE.Vector2(x, yy)),
            48,
          ),
          m,
          cx,
          y,
          cz,
        );
      lathe(
        [
          [0, 0],
          [2.7, 0],
          [2.8, 0.12],
          [2.8, 0.55],
          [2.62, 0.62],
          [2.5, 0.3],
          [0, 0.3],
        ],
        stoneSmooth,
        0.37,
      );
      add(new THREE.CircleGeometry(2.52, 48).rotateX(-PI / 2), waterMat, cx, 0.82, cz).castShadow =
        false;
      add(new THREE.CylinderGeometry(0.22, 0.32, 1.6, 24), stoneSmooth, cx, 1.5, cz);
      lathe(
        [
          [0, 0],
          [0.3, 0],
          [0.95, 0.22],
          [1.05, 0.34],
          [0.98, 0.4],
          [0.25, 0.18],
          [0, 0.18],
        ],
        stoneSmooth,
        2.25,
      );
      add(new THREE.CircleGeometry(0.92, 32).rotateX(-PI / 2), waterMat, cx, 2.6, cz).castShadow =
        false;
      add(new THREE.CylinderGeometry(0.08, 0.12, 0.5, 16), stoneSmooth, cx, 2.8, cz);
      add(new THREE.SphereGeometry(0.14, 16, 12), stoneSmooth, cx, 3.1, cz);
      jets.push({ cx, cz });
      colliders.push({ type: 'ell', cx, cz, a: 2.9, b: 2.9 });
    }
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * PI * 2 + 0.2,
        r = pond ? 10.2 : 6.5;
      benches.push({
        x: cx + Math.cos(a) * r * (pond ? 0.97 : 0.58),
        z: cz + Math.sin(a) * r * (pond ? 0.7 : 0.58),
        ry: Math.atan2(-Math.cos(a), -Math.sin(a)),
      });
    }
  };

  // ---------- Eixample ----------
  trees = [];
  colliders = [];
  BLOCK = 22;
  CH = 4;
  HOLE = 12;
  HCH = 2;
  PLATE = 25.5;
  PCH = 4.8;
  wins = [];
  shops = [];
  containers = [];
  blocks = [];
  benches = [];
  plaques = [];
  parked = [];
  roof = {
    huts: [],
    tanks: [],
    antennas: [],
    dishes: [],
    panels: [],
    vents: [],
    laundry: [],
    chimneys: [],
    wells: [],
  };
  SHOPS = [
    ['FARMÀCIA', '#1f8a4c'],
    ['FORN DE PA', '#8a4b1f'],
    ['BAR', '#b8322a'],
    ['FRUITES', '#3f8f3a'],
    ['FERRETERIA', '#2f4f7f'],
    ['LLIBRERIA', '#5b3b7a'],
    ['CAFÈ', '#3b2a20'],
    ['PERRUQUERIA', '#c2457a'],
    ['SUPERMERCAT', '#d4661a'],
    ['RESTAURANT', '#7a1f2b'],
  ];
  const XST = { '-45': 'Sicília', '-15': 'Sardenya', 15: 'la Marina', 45: 'Lepant' },
    ZST = { '-45': 'Rosselló', '-15': 'Provença', 15: 'Mallorca', 45: 'València' };
  // windows (with optional iron balcony) and shopfronts along one façade edge, on a shared bay grid
  function facade(ax, az, bx, bz, nx, nz, y0, y1, withShops) {
    const len = Math.hypot(bx - ax, bz - az),
      bays = Math.max(1, Math.floor(len / 1.3)),
      ry = Math.atan2(nx, nz),
      balc = rnd() < 0.8,
      door = bays >= 4 ? 1 + Math.floor(rnd() * (bays - 2)) : -1;
    for (let i = 0; i < bays; i++) {
      const t = (i + 0.5) / bays,
        x = ax + (bx - ax) * t + nx * 0.14,
        z = az + (bz - az) * t + nz * 0.14;
      if (withShops)
        shops.push(
          i === door
            ? { x, z, ry, door: true, awning: false, sign: -1 }
            : {
                x,
                z,
                ry,
                awning: rnd() < 0.45,
                sign: rnd() < 0.3 ? Math.floor(rnd() * SHOPS.length) : -1,
              },
        );
      for (let y = y0; y + 0.4 < y1; y += 0.9)
        wins.push({
          x,
          z,
          y,
          ry,
          b: balc && rnd() < 0.85,
          lit: rnd() < 0.4,
          sh: rnd() < 0.45,
          roll: rnd() < 0.45 ? rr(0.12, 0.55) : 0,
          ac: rnd() < 0.06,
          pots: rnd() < 0.3,
          flag: rnd() < 0.035,
          hang: rnd() < 0.12,
        });
    }
  }
  tufts = [];
  flowers = [];
  hedges = [];
  pads = [];
  palmSpots = [];
  ducks = [];
  jets = [];
  // distance from the centre to an octagon (half size H, chamfer C) along angle th
  function octR(th, H, C) {
    const c = Math.abs(Math.cos(th)),
      s = Math.abs(Math.sin(th));
    return Math.min(H / Math.max(c, 1e-6), H / Math.max(s, 1e-6), (2 * H - C) / (c + s));
  }
}
